/**
 * ToolsPortalApp – Standalone-App für tools.sijelly.com (externe Tool-Nutzer).
 *
 * Eigener Login gegen /tools-portal/auth (Tool-Benutzer, NICHT Mitarbeiter).
 * Nach dem Login: genau 1 Tool → direkt rendern; mehrere → Kachel-Auswahl
 * mit schlanker Topbar (Tool-Wechsel, Name · Firma, Abmelden).
 */
import React, { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import { buildToolTiles } from "./toolRegistry";
import { clearToolToken, getToolToken, setToolToken } from "./portalAuth";
import "./ToolsPortalApp.css";

export default function ToolsPortalApp() {
  const [user, setUser] = useState(null); // {login, name, firma, tools:[...]}
  const [ready, setReady] = useState(false);
  const [loginForm, setLoginForm] = useState({ login: "", passwort: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTool, setActiveTool] = useState(null); // tile.id
  // Versionsnummer wie in der imho-Navbar — aus RELEASE.json des Builds
  const [version, setVersion] = useState("");
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/RELEASE.json`, { cache: "no-store" })
      .then((r) => r.json())
      .then((d) => setVersion(d.version || ""))
      .catch(() => {});
  }, []);

  // Eigener Tab-Titel fürs Portal (Standard-HTML trägt den imho-Titel)
  useEffect(() => {
    document.title = "Sijelly Tools";
  }, []);

  // Beim Start: gespeichertes Token prüfen
  useEffect(() => {
    const token = getToolToken();
    if (!token) { setReady(true); return; }
    fetch(`${API_BASE}/tools-portal/auth/me`, { headers: { "X-Tool-Token": token } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setUser(data);
        else clearToolToken();
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await fetch(`${API_BASE}/tools-portal/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Login fehlgeschlagen");
      setToolToken(data.token);
      setUser(data);
    } catch (err) {
      setLoginError(String(err.message || err));
    } finally {
      setLoggingIn(false);
    }
  }, [loginForm]);

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/tools-portal/auth/logout`, {
        method: "POST",
        headers: { "X-Tool-Token": getToolToken() },
      });
    } catch (_) {}
    clearToolToken();
    localStorage.removeItem("portalMode");
    setUser(null);
    setActiveTool(null);
  }, []);

  if (!ready) return <div className="tp-loading">Lädt…</div>;

  // ── Login ────────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="tp-login-wrap">
        <form className="tp-login-box" onSubmit={handleLogin}>
          <div className="tp-login-logo">🧰</div>
          <h1 className="tp-login-title">Sijelly Tools</h1>
          {version && <div className="tp-version tp-version-login">v{version}</div>}
          {loginError && <div className="tp-login-error">{loginError}</div>}
          <input
            className="tp-input"
            placeholder="Benutzername"
            autoCapitalize="none"
            autoCorrect="off"
            value={loginForm.login}
            onChange={(e) => setLoginForm((f) => ({ ...f, login: e.target.value }))}
          />
          <input
            className="tp-input"
            type="password"
            placeholder="Passwort"
            value={loginForm.passwort}
            onChange={(e) => setLoginForm((f) => ({ ...f, passwort: e.target.value }))}
          />
          <button className="tp-btn tp-btn-login" type="submit" disabled={loggingIn}>
            {loggingIn ? "…" : "Anmelden"}
          </button>
        </form>
      </div>
    );
  }

  // ── Angemeldet ───────────────────────────────────────────────────────────
  const tiles = buildToolTiles(user.tools);
  const current =
    tiles.length === 1 ? tiles[0] : tiles.find((t) => t.id === activeTool) || null;

  const userLabel = [user.name || user.login, user.firma].filter(Boolean).join(" · ");

  return (
    <div className="tp-app">
      <div className="tp-topbar">
        <div className="tp-topbar-left">
          <span className="tp-brand">🧰 Sijelly Tools</span>
          {version && <span className="tp-version">v{version}</span>}
          {tiles.length > 1 &&
            tiles.map((t) => (
              <button
                key={t.id}
                className={`tp-tab ${current?.id === t.id ? "active" : ""}`}
                onClick={() => setActiveTool(t.id)}
              >
                {t.icon} {t.label}
              </button>
            ))}
        </div>
        <div className="tp-topbar-right">
          <span className="tp-user">{userLabel}</span>
          <button className="tp-btn tp-btn-ghost" onClick={handleLogout}>Abmelden</button>
        </div>
      </div>

      {tiles.length === 0 && (
        <div className="tp-empty">
          Für deinen Zugang ist noch kein Tool freigeschaltet.
          <br />Bitte melde dich beim Ansprechpartner von Sijelly.
        </div>
      )}

      {!current && tiles.length > 1 && (
        <div className="tp-tiles">
          {tiles.map((t) => (
            <div key={t.id} className="tp-tile" onClick={() => setActiveTool(t.id)}>
              <div className="tp-tile-icon">{t.icon}</div>
              <div className="tp-tile-label">{t.label}</div>
            </div>
          ))}
        </div>
      )}

      {current && <div className="tp-content">{current.render()}</div>}
    </div>
  );
}
